import { fetchAuthSession } from '@aws-amplify/core';

import api from '../axios';

/* =========================================================
   ENUM VALUES
   ========================================================= */

export const DELIVERY_CLASSIFICATIONS = [
  'NORMAL',
  'SUSPICIOUS',
  'THREAT',
  'PURSUER_MAIL',
] as const;

export const DELIVERY_STATUSES = ['DRAFT', 'SUBMITTED'] as const;

export const DELIVERY_COMPANIES = [
  'AMAZON',
  'DHL',
  'FEDEX',
  'UPS',
  'USPS',
  'COURIER',
  'HAND_DELIVERED',
  'OTHER',
  'UNKNOWN',
] as const;

/* =========================================================
   TYPES
   ========================================================= */

export type DeliveryClassification = (typeof DELIVERY_CLASSIFICATIONS)[number];

export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

export type DeliveryCompany = (typeof DELIVERY_COMPANIES)[number];

/* =========================================================
   IMAGE
   ========================================================= */

export type DeliveryImage = {
  storageKey: string;

  fileName?: string;

  mimeType?: string;

  size?: number;

  uploadedAt?: string;

  uploadedByUserId?: string;

  /*
   * Temporary signed S3 URL returned by detail endpoints.
   */
  url?: string;
};

/* =========================================================
   DELIVERY RECORD
   ========================================================= */

export type Delivery = {
  _id: string;

  /*
   * SERVER-AUTHORITATIVE.
   *
   * Never send these as ownership inputs from the browser.
   */
  associatedCompany: string;

  associatedSite: string;

  status: DeliveryStatus;

  classification: DeliveryClassification;

  receivedAt?: string;

  deliveryCompany?: DeliveryCompany;

  deliveryCompanyOther?: string;

  /*
   * Intended recipient.
   */
  recipientId?: string;

  recipientName?: string;

  recipientPhoneSnapshot?: string;

  /*
   * Person who received/logged the package.
   *
   * Backend derives this from the authenticated user.
   */
  receivedByUserId?: string;

  receivedByNameSnapshot?: string;

  image?: DeliveryImage;

  notes?: string;

  notifyRecipientRequested: boolean;

  recipientNotifiedAt?: string;

  recipientNotificationId?: string;

  createdByUserId?: string;

  createdByNameSnapshot?: string;

  lastEditedByUserId?: string;

  submittedByUserId?: string;

  submittedAt?: string;

  createdAt: string;

  updatedAt: string;
};

/* =========================================================
   CONFIG
   ========================================================= */

export type DeliveryConfig = {
  classifications: readonly DeliveryClassification[];

  statuses: readonly DeliveryStatus[];

  deliveryCompanies: readonly DeliveryCompany[];

  imageRequiredFor: readonly DeliveryClassification[];
};

/* =========================================================
   CREATE
   ========================================================= */

export type CreateDeliveryPayload = {
  /*
   * Only site ownership value sent by frontend.
   *
   * Backend resolves:
   *
   * siteId
   *   ↓
   * authorized Site
   *   ↓
   * associatedCompany
   *   ↓
   * associatedSite
   */
  siteId: string;

  classification: DeliveryClassification;

  receivedAt?: string;

  deliveryCompany?: DeliveryCompany;

  deliveryCompanyOther?: string;

  recipientId?: string;

  recipientName?: string;

  notes?: string;

  notifyRecipientRequested?: boolean;
};

/* =========================================================
   UPDATE
   ========================================================= */

export type UpdateDeliveryPayload = {
  classification?: DeliveryClassification;

  receivedAt?: string | null;

  deliveryCompany?: DeliveryCompany | null;

  deliveryCompanyOther?: string | null;

  recipientId?: string | null;

  recipientName?: string | null;

  notes?: string | null;

  notifyRecipientRequested?: boolean;
};

/* =========================================================
   HISTORY FILTERS
   ========================================================= */

export type GetDeliveriesFilters = {
  siteId: string;

  from?: string;

  to?: string;

  classification?: DeliveryClassification;

  deliveryCompany?: DeliveryCompany;

  status?: DeliveryStatus;

  recipientName?: string;

  receivedByUserId?: string;

  limit?: number;

  skip?: number;
};

/* =========================================================
   HISTORY RESPONSE
   ========================================================= */

export type DeliveryHistoryResult = {
  deliveries: Delivery[];

  total: number;

  limit: number;

  skip: number;
};

/* =========================================================
   GENERIC API RESPONSE
   ========================================================= */

export type ApiResponse<T = any> = {
  success: boolean;

  data?: T;

  msg?: string;
};

/* =========================================================
   AUTH
   ========================================================= */

async function getIdToken(): Promise<string> {
  const { tokens } = await fetchAuthSession();

  const idToken = tokens?.idToken?.toString();

  if (!idToken) {
    throw new Error('NO SESSION');
  }

  return idToken;
}

function authHeaders(idToken: string) {
  return {
    Authorization: `Bearer ${idToken}`,
  };
}

/* =========================================================
   GET CONFIG
   ========================================================= */

export async function getDeliveryConfig(): Promise<
  ApiResponse<DeliveryConfig>
> {
  const idToken = await getIdToken();

  const res = await api.get(
    '/deliveries/config',

    {
      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    /*
     * DeliveryController currently returns:
     *
     * {
     *   success: true,
     *   data: {
     *     classifications,
     *     statuses,
     *     deliveryCompanies,
     *     imageRequiredFor
     *   }
     * }
     */
    data: raw?.data,
  };
}

/* =========================================================
   CREATE DELIVERY DRAFT
   ========================================================= */

export async function createDelivery(
  payload: CreateDeliveryPayload
): Promise<ApiResponse<Delivery>> {
  const idToken = await getIdToken();

  const res = await api.post(
    '/deliveries',

    {
      ...payload,

      /*
       * Keep this consistent with the existing
       * authenticated JSON API calls.
       */
      token: idToken,
    },

    {
      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data?.delivery,
  };
}

/* =========================================================
   UPDATE DELIVERY DRAFT
   ========================================================= */

export async function updateDeliveryDraft(
  deliveryId: string,

  siteId: string,

  payload: UpdateDeliveryPayload
): Promise<ApiResponse<Delivery>> {
  if (!deliveryId) {
    throw new Error('deliveryId is required.');
  }

  if (!siteId) {
    throw new Error('siteId is required.');
  }

  const idToken = await getIdToken();

  const res = await api.patch(
    `/deliveries/${encodeURIComponent(deliveryId)}`,

    {
      siteId,

      ...payload,

      token: idToken,
    },

    {
      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data?.delivery,
  };
}

/* =========================================================
   GET DELIVERY
   ========================================================= */

export async function getDeliveryById(
  deliveryId: string,

  siteId: string
): Promise<ApiResponse<Delivery>> {
  if (!deliveryId) {
    throw new Error('deliveryId is required.');
  }

  if (!siteId) {
    throw new Error('siteId is required.');
  }

  const idToken = await getIdToken();

  const res = await api.get(
    `/deliveries/${encodeURIComponent(deliveryId)}`,

    {
      params: {
        siteId,
      },

      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data?.delivery,
  };
}

/* =========================================================
   DELIVERY HISTORY
   ========================================================= */

export async function getDeliveries(
  filters: GetDeliveriesFilters
): Promise<ApiResponse<DeliveryHistoryResult>> {
  if (!filters.siteId) {
    throw new Error('siteId is required.');
  }

  const idToken = await getIdToken();

  const params: Record<string, string | number> = {
    siteId: filters.siteId,
  };

  if (filters.from) {
    params.from = filters.from;
  }

  if (filters.to) {
    params.to = filters.to;
  }

  if (filters.classification) {
    params.classification = filters.classification;
  }

  if (filters.deliveryCompany) {
    params.deliveryCompany = filters.deliveryCompany;
  }

  if (filters.status) {
    params.status = filters.status;
  }

  if (filters.recipientName) {
    params.recipientName = filters.recipientName;
  }

  if (filters.receivedByUserId) {
    params.receivedByUserId = filters.receivedByUserId;
  }

  if (filters.limit !== undefined) {
    params.limit = filters.limit;
  }

  if (filters.skip !== undefined) {
    params.skip = filters.skip;
  }

  const res = await api.get(
    '/deliveries',

    {
      params,

      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  const result = raw?.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: {
      deliveries: Array.isArray(result?.deliveries) ? result.deliveries : [],

      total: Number(result?.total ?? 0),

      limit: Number(result?.limit ?? filters.limit ?? 100),

      skip: Number(result?.skip ?? filters.skip ?? 0),
    },
  };
}

/* =========================================================
   UPLOAD DELIVERY IMAGE
   ========================================================= */

export async function uploadDeliveryImage(
  deliveryId: string,

  siteId: string,

  file: File
): Promise<ApiResponse<Delivery>> {
  if (!deliveryId) {
    throw new Error('deliveryId is required.');
  }

  if (!siteId) {
    throw new Error('siteId is required.');
  }

  if (!file) {
    throw new Error('Delivery image is required.');
  }

  /*
   * Frontend validation is convenience only.
   *
   * Backend/S3 validation remains authoritative.
   */

  if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
    throw new Error('Delivery image must be a JPG or PNG file.');
  }

  const maxSize = 10 * 1024 * 1024;

  if (file.size > maxSize) {
    throw new Error('Delivery image cannot exceed 10 MB.');
  }

  const idToken = await getIdToken();

  const formData = new FormData();

  formData.append('siteId', siteId);

  /*
   * Must match:
   *
   * upload.single("image")
   *
   * in DeliveryController.
   */
  formData.append('image', file, file.name);

  /*
   * IMPORTANT:
   *
   * Do NOT manually set Content-Type.
   *
   * Axios/browser must generate:
   *
   * multipart/form-data;
   * boundary=...
   */
  const res = await api.post(
    `/deliveries/${encodeURIComponent(deliveryId)}/image`,

    formData,

    {
      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data?.delivery,
  };
}

/* =========================================================
   DELETE DELIVERY IMAGE
   ========================================================= */

export async function deleteDeliveryImage(
  deliveryId: string,

  siteId: string
): Promise<ApiResponse<Delivery>> {
  if (!deliveryId) {
    throw new Error('deliveryId is required.');
  }

  if (!siteId) {
    throw new Error('siteId is required.');
  }

  const idToken = await getIdToken();

  const res = await api.delete(
    `/deliveries/${encodeURIComponent(deliveryId)}/image`,

    {
      params: {
        siteId,
      },

      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data?.delivery,
  };
}

/* =========================================================
   SUBMIT DELIVERY
   ========================================================= */

export async function submitDelivery(
  deliveryId: string,

  siteId: string
): Promise<ApiResponse<Delivery>> {
  if (!deliveryId) {
    throw new Error('deliveryId is required.');
  }

  if (!siteId) {
    throw new Error('siteId is required.');
  }

  const idToken = await getIdToken();

  const res = await api.post(
    `/deliveries/${encodeURIComponent(deliveryId)}/submit`,

    {
      siteId,

      token: idToken,
    },

    {
      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data?.delivery,
  };
}

/* =========================================================
   DELETE DELIVERY DRAFT
   ========================================================= */

export async function deleteDeliveryDraft(
  deliveryId: string,

  siteId: string
): Promise<
  ApiResponse<{
    deliveryId: string;
  }>
> {
  if (!deliveryId) {
    throw new Error('deliveryId is required.');
  }

  if (!siteId) {
    throw new Error('siteId is required.');
  }

  const idToken = await getIdToken();

  const res = await api.delete(
    `/deliveries/${encodeURIComponent(deliveryId)}`,

    {
      params: {
        siteId,
      },

      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),

    msg: raw?.msg,

    data: raw?.data,
  };
}
